import { useEffect, useState } from 'react';
import { Bell, BellOff, Loader2 } from 'lucide-react';
import { Capacitor } from '@capacitor/core';
import { LocalNotifications } from '@capacitor/local-notifications';
import {
  getPermissionStatus,
  playAdhan,
  playChime,
  requestNotificationPermission,
  stopAdhan,
  type NotificationPrefs,
  type NotifSound,
} from '../utils/notifications';
import { getButtonStyles, getIconColor } from '../utils/themeUtils';
import type { Language, Theme } from '../types';

interface Props {
  isDark: boolean;
  theme: Theme;
  language: Language;
  prefs: NotificationPrefs;
  onChange: (p: NotificationPrefs) => void;
}

export function NotificationSettings({ isDark, theme, language, prefs, onChange }: Props) {
  const id = language === 'id';
  const [perm, setPerm] = useState<string>('unknown');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [playingPreview, setPlayingPreview] = useState<'adhan' | 'full' | null>(null);

  useEffect(() => {
    getPermissionStatus().then(setPerm).catch(() => {});
    return () => stopAdhan();
  }, []);

  const handleToggle = async () => {
    setError(null);
    if (!prefs.enabled) {
      setBusy(true);
      const ok = await requestNotificationPermission();
      setPerm(await getPermissionStatus());
      setBusy(false);
      if (!ok) {
        setError(id ? 'Izin notifikasi ditolak. Aktifkan di Pengaturan HP > Aplikasi > The Five.' : 'Notification permission denied. Enable it in phone Settings > Apps > The Five.');
        return;
      }
      onChange({ ...prefs, enabled: true });
    } else {
      onChange({ ...prefs, enabled: false });
    }
  };

  const handleTest = async () => {
    setError(null);
    try {
      // Preview langsung tanpa perlu izin notifikasi
      if (prefs.sound === 'chime') {
        await playChime();
        return;
      }
      if (prefs.sound === 'adhan') {
        if (playingPreview) {
          stopAdhan();
          setPlayingPreview(null);
        } else {
          setPlayingPreview('adhan');
          const audio = playAdhan(false);
          audio.onended = () => setPlayingPreview(null);
        }
        return;
      }
      const ok = await requestNotificationPermission();
      if (!ok) {
        setError(id ? 'Izin notifikasi belum diberikan.' : 'Notification permission not granted.');
        return;
      }
      if (Capacitor.isNativePlatform()) {
        await LocalNotifications.schedule({
          notifications: [
            {
              id: 999,
              title: 'Tes Notifikasi 🕌',
              body: id ? 'Notifikasi adzan berfungsi!' : 'Adhan notifications working!',
              schedule: { at: new Date(Date.now() + 5000), allowWhileIdle: true },
              channelId: prefs.sound === 'silent' ? 'adhan-silent' : 'adhan',
            },
          ],
        });
      } else if ('Notification' in window) {
        setTimeout(() => {
          new Notification('Tes Notifikasi 🕌', {
            body: id ? 'Notifikasi adzan berfungsi!' : 'Adhan notifications working!',
            silent: prefs.sound === 'silent',
          });
        }, 2000);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Gagal tes notifikasi');
    }
  };

  return (
    <div className={`rounded-xl shadow-lg ${isDark ? 'bg-gray-800' : 'bg-white'} p-4 sm:p-6 mb-6 sm:mb-8`}>
      <div className="flex items-center gap-3 mb-4">
        <Bell className="w-5 h-5" style={getIconColor(theme)} />
        <h2 className="text-base sm:text-lg font-semibold">
          {id ? 'Pengingat Adzan' : 'Adhan Reminders'}
        </h2>
        <span className={`ml-auto text-xs px-2 py-1 rounded-full ${perm === 'granted' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
          {perm}
          {Capacitor.isNativePlatform() ? ` • ${Capacitor.getPlatform()}` : ' • web'}
        </span>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        <button
          onClick={handleToggle}
          disabled={busy}
          className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-white font-medium transition-colors disabled:opacity-50 min-h-[48px]"
          style={getButtonStyles(theme)}
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : prefs.enabled ? <BellOff className="w-4 h-4" /> : <Bell className="w-4 h-4" />}
          {prefs.enabled ? (id ? 'Matikan' : 'Turn off') : (id ? 'Aktifkan Notifikasi' : 'Enable Notifications')}
        </button>

        <label className={`flex items-center gap-2 text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
          {id ? 'Ingatkan' : 'Remind me'}
          <select
            value={prefs.beforeMinutes}
            onChange={(e) => onChange({ ...prefs, beforeMinutes: Number(e.target.value) })}
            className={`px-3 py-2.5 rounded-xl border min-h-[48px] text-base ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
          >
            <option value={0}>{id ? 'Tepat waktu' : 'On time'}</option>
            <option value={5}>5 {id ? 'mnt sblm' : 'min before'}</option>
            <option value={10}>10 {id ? 'mnt sblm' : 'min before'}</option>
            <option value={15}>15 {id ? 'mnt sblm' : 'min before'}</option>
          </select>
        </label>

        <label className={`flex items-center gap-2 text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
          {id ? 'Suara' : 'Sound'}
          <select
            value={prefs.sound}
            onChange={(e) => onChange({ ...prefs, sound: e.target.value as NotifSound })}
            className={`px-3 py-2.5 rounded-xl border min-h-[48px] text-base ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
          >
            <option value="adhan">🕌 {id ? 'Adzan' : 'Adhan'}</option>
            <option value="system">{id ? 'Sistem HP' : 'Phone default'}</option>
            <option value="chime">Chime</option>
            <option value="silent">{id ? 'Hening' : 'Silent'}</option>
          </select>
        </label>

        <button
          onClick={handleTest}
          className={`px-4 py-2.5 rounded-xl text-sm font-medium min-h-[48px] ${isDark ? 'bg-gray-700 text-gray-200' : 'bg-gray-100 text-gray-700'}`}
        >
          {prefs.sound === 'chime'
            ? (id ? 'Dengar Chime' : 'Preview Chime')
            : prefs.sound === 'adhan'
            ? playingPreview
              ? (id ? '⏹ Berhenti' : '⏹ Stop')
              : (id ? '▶ Dengar Adzan' : '▶ Preview Adhan')
            : (id ? 'Tes (5 dtk)' : 'Test (5s)')}
        </button>
      </div>

      {prefs.sound === 'adhan' && (
        <button
          onClick={() => {
            if (playingPreview === 'full') {
              stopAdhan();
              setPlayingPreview(null);
            } else {
              setPlayingPreview('full');
              const audio = playAdhan(true);
              audio.onended = () => setPlayingPreview(null);
            }
          }}
          className={`mt-3 text-sm underline ${isDark ? 'text-gray-300' : 'text-gray-600'}`}
        >
          {playingPreview === 'full'
            ? (id ? '⏹ Berhenti adzan penuh' : '⏹ Stop full adhan')
            : (id ? '▶ Putar adzan penuh' : '▶ Play full adhan')}
        </button>
      )}

      <label className={`flex items-center gap-3 mt-4 cursor-pointer select-none ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
        <button
          role="switch"
          aria-checked={prefs.skipCompleted}
          onClick={() => onChange({ ...prefs, skipCompleted: !prefs.skipCompleted })}
          className={`relative w-12 h-7 rounded-full transition-colors flex-shrink-0 ${prefs.skipCompleted ? '' : isDark ? 'bg-gray-600' : 'bg-gray-300'}`}
          style={prefs.skipCompleted ? getButtonStyles(theme) : {}}
        >
          <span className={`absolute top-1 w-5 h-5 rounded-full bg-white shadow transition-all ${prefs.skipCompleted ? 'right-1' : 'left-1'}`} />
        </button>
        <span className="text-sm">
          {id ? 'Jangan bunyikan sholat yang sudah dicentang ✓' : "Skip prayers already marked done ✓"}
        </span>
      </label>

      {error && <p className="text-red-500 text-sm mt-3">{error}</p>}

      <p className={`text-xs mt-3 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
        {Capacitor.isNativePlatform()
          ? (id ? 'Notifikasi dijadwalkan ulang tiap jadwal berubah & tiap hari. Tetap bunyi walau aplikasi ditutup.' : 'Notifications reschedule when times change & daily. They fire even when the app is closed.')
          : (id ? 'Di web/PWA notifikasi hanya muncul saat aplikasi terbuka. Untuk hasil terbaik install sebagai aplikasi native (Android/iOS).' : 'On web/PWA notifications only fire while the app is open. For best results use the native Android/iOS build.')}
      </p>
      <p className={`text-[11px] mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
        🔊 {id ? 'Audio adzan: Internet Archive (Public Domain Mark 1.0).' : 'Adhan audio: Internet Archive (Public Domain Mark 1.0).'}
      </p>
    </div>
  );
}
